/**
 * The two backend calls this page makes. Both are authenticated with the viewer's Clerk session
 * token — which is the whole point: the backend learns *who* is approving from a token it
 * validates against Clerk's JWKS, never from anything this page asserts.
 */

const API_URL: string = import.meta.env.VITE_API_URL ?? '';

/**
 * Mirrors the backend's `DeviceAuthorizationStatus`. The API serialises enums as integers by
 * convention (see its OpenAPI `EnumSchemaTransformer`), so these are wire values.
 */
export const PairingStatus = {
  Pending: 0,
  Approved: 1,
  Denied: 2,
  Redeemed: 3,
} as const;

export type PairingStatusValue = (typeof PairingStatus)[keyof typeof PairingStatus];

export type Pairing = {
  userCode: string;
  /** Shown to the viewer so they confirm a device, not a bare code. Null when the TV sent none. */
  deviceName: string | null;
  channelKey: string | null;
  status: PairingStatusValue;
  expiresAt: string;
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** A code that never existed, already expired, or was already used — one message covers all. */
  get isUnknownCode(): boolean {
    return this.status === 404;
  }
}

async function request<T>(path: string, token: string, init?: RequestInit): Promise<T | null> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}/api/auth/device${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...init?.headers,
      },
    });
  } catch (cause) {
    // fetch only rejects on transport failure — including a CORS rejection, which is the most
    // likely first-run problem here (the backend's AllowedOrigins must list this app's origin).
    throw new ApiError(0, 'Geen verbinding met de server.');
  }

  if (!response.ok) {
    // The API answers errors as ProblemDetails; `detail` carries the human-readable reason.
    const problem = (await response.json().catch(() => null)) as { detail?: string } | null;
    throw new ApiError(response.status, problem?.detail ?? `Serverfout (${response.status}).`);
  }

  // 204 No Content (deny) has no body to parse.
  return response.status === 204 ? null : ((await response.json()) as T);
}

export function getPairing(userCode: string, token: string): Promise<Pairing | null> {
  return request<Pairing>(`/${encodeURIComponent(userCode)}`, token);
}

export function approvePairing(userCode: string, token: string): Promise<{ deviceName: string | null } | null> {
  return request<{ deviceName: string | null }>('/approve', token, {
    method: 'POST',
    body: JSON.stringify({ userCode }),
  });
}

export function denyPairing(userCode: string, token: string): Promise<null> {
  return request<null>('/deny', token, {
    method: 'POST',
    body: JSON.stringify({ userCode }),
  }) as Promise<null>;
}
